import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { useQuery } from "react-query";
import { getLinkTranscriptRequest } from "../../api/queries/links/getTranscript";
import "./TranscriptModal.css";

interface TranscriptModalProps {
  linkId: string;
  linkTitle?: string;
  visible: boolean;
  onHide: () => void;
}

export const TranscriptModal = ({
  linkId,
  linkTitle,
  visible,
  onHide,
}: TranscriptModalProps) => {
  const {
    data: transcript,
    isLoading,
    error,
  } = useQuery(["transcript", linkId], () => getLinkTranscriptRequest(linkId), {
    enabled: visible,
    retry: false,
  });

  const formatTranscript = (transcriptText: string) => {
    return <div className="line-height-3">{transcriptText}</div>;
  };

  const headerElement = (
    <div className="flex align-items-center">
      <i className="pi pi-file-o mr-2 text-primary"></i>
      <span className="font-semibold">
        Transcript {linkTitle && `- ${linkTitle}`}
      </span>
    </div>
  );

  const footerElement = (
    <div>
      <Button
        label="Close"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
        autoFocus
      />
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-content-center align-items-center p-4">
          <ProgressSpinner
            style={{ width: "50px", height: "50px" }}
            strokeWidth="8"
          />
          <span className="ml-3">Loading transcript...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4 border-1 border-round surface-100 text-center">
          <i className="pi pi-exclamation-triangle text-orange-500 text-2xl mb-2"></i>
          <p className="text-color-secondary mb-0">
            {error instanceof Error
              ? error.message
              : "Failed to load transcript. This video may not have a transcript available."}
          </p>
        </div>
      );
    }

    if (transcript) {
      return (
        <div className="transcript-text">
          <div
            className="p-4 border-1 border-round surface-50 overflow-auto"
            style={{ maxHeight: "60vh" }}
          >
            <div className="text-sm text-color-secondary mb-3 pb-2 border-bottom-1 surface-border">
              <i className="pi pi-calendar mr-2"></i>
              Downloaded on:{" "}
              {new Date(transcript.data.created_at).toLocaleDateString()}
            </div>

            <div className="text-color">
              {formatTranscript(transcript.data.clean_transcript)}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog
      header={headerElement}
      visible={visible}
      style={{ width: "70vw", maxWidth: "800px" }}
      footer={footerElement}
      onHide={onHide}
      modal
      draggable={false}
      resizable={false}
      className="transcript-dialog"
    >
      <div className="transcript-content">{renderContent()}</div>
    </Dialog>
  );
};
